/* The decisions the one-tap identify screen makes, lifted out of the component.
 *
 * WHY A SEPARATE MODULE
 * ---------------------
 * The screen itself is a viewfinder and a result card -- easy to eyeball, hard
 * to unit-test, and nobody notices when it quietly stops doing the one thing
 * that matters. What matters is this:
 *
 *   - a dangerous find must never be drawn in the calm, friendly style
 *   - a warning must always be on screen
 *   - "this is a mushroom, and mushrooms are Premium" must never be flattened
 *     into "something went wrong" or into "buy more AI credits"
 *
 * Those three are decisions, not pixels, so they live here where a test can
 * hold them still. The server already enforces its half of this contract in
 * `services/identify_any.py`; this is the client half, and the two are
 * deliberately belt-and-braces rather than one trusting the other.
 */

/** Visual severity. Ordered: each one is louder than the last. */
export const TONES = ['calm', 'caution', 'danger'];

const DEADLY_OR_TOXIC = new Set(['toxic', 'deadly']);

/**
 * How loudly to draw a result.
 *
 * Note what this does NOT do: it does not re-derive risk from `kind`. The
 * server owns the kind -> risk rule and ships the answer as `high_risk`;
 * a second copy of that list on the client is a second copy to forget to
 * update. The client's job is only to never draw a risky answer quietly.
 */
export function toneFor(result) {
    if (!result || typeof result !== 'object') return 'caution';
    const edibility = String(result.edibility || '');
    const lookalikes = Array.isArray(result.lookalikes) ? result.lookalikes : [];
    const deadlyLookalike = lookalikes.some((l) => l && l.danger === 'deadly');
    if (DEADLY_OR_TOXIC.has(edibility) || deadlyLookalike) return 'danger';
    // Calm is the branch that needs evidence, not the fall-through one. The
    // server states `high_risk` as a boolean on every answer, so anything that
    // does not carry a plain `false` -- a truncated reply, a shape from a newer
    // version, an array where an object was expected -- is drawn with caution.
    // Written the other way round (fall through TO calm) an unrecognised shape
    // gets the friendly green treatment, which is the exact failure this
    // function exists to prevent.
    const knownSafeKind = result.high_risk === false;
    if (knownSafeKind && lookalikes.length === 0 && edibility !== 'edible_with_care') return 'calm';
    return 'caution';
}

/**
 * The warning to display.
 *
 * The server guarantees a non-empty `warning` on every answer. This is the
 * client's own floor under that guarantee: if a future change ever lets an
 * empty one through, the screen shows the general caution rather than a blank
 * space where a safety line used to be. A screen that silently drops its
 * warning is worse than one that crashes.
 */
export function warningFor(result, fallback) {
    const warning = String((result && result.warning) || '').trim();
    return warning || String(fallback || '').trim();
}

/** Percent 0-100 as a whole number, or null when the server sent nothing usable. */
export function confidenceFor(result) {
    const raw = result && result.confidence;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Turn a failed request into a state the screen can actually draw.
 *
 * Every branch here exists because the generic "could not analyse" toast is
 * wrong for it, and wrong in a way that costs the user something: sending
 * somebody to buy AI credits when what they need is Premium, or showing an
 * error when they themselves cancelled the price dialog.
 */
export function stateFromError(error) {
    // The AI price dialog was declined. The user made a choice; that is not a
    // failure, and an error card would read as one.
    if (error && error.code === 'ERR_CANCELED') return { kind: 'cancelled' };

    const response = (error && error.response) || {};
    const status = response.status;
    const detail = response.data && response.data.detail;
    const isObject = detail && typeof detail === 'object';

    if (status === 402 && isObject && detail.reason === 'mushroom_premium') {
        // The most important branch in this function. The server identified a
        // fungus and withheld the name on purpose, so this is NOT an error and
        // NOT a credits problem -- it is a real answer ("it's a mushroom") plus
        // the reason the rest is held back. It still carries the warning.
        return {
            kind: 'premium_mushroom',
            warning: String(detail.warning || '').trim(),
            message: String(detail.message || '').trim(),
        };
    }
    if (status === 402) return { kind: 'quota' };
    if (status === 401 || status === 403) return { kind: 'signin' };
    if (status === 404 || status === 501) {
        // The app asked for an endpoint the server does not have. That is not
        // "something went wrong" -- it is a version mismatch, and saying so is
        // the difference between a user retrying forever and a developer
        // knowing to deploy. It happens to anyone running an app build that is
        // ahead of the server.
        return { kind: 'not_deployed' };
    }
    if (!error || !error.response) return { kind: 'offline' };
    return { kind: 'error', message: typeof detail === 'string' ? detail : '' };
}

/**
 * Whether this really is an answer from the identify endpoint.
 *
 * `api.post` resolving is not proof of anything. A build without
 * `REACT_APP_BACKEND_URL` makes the API path relative, and inside the app the
 * origin is the bundled web layer -- so `/api/ai/identify-any` hits the app's
 * own asset server, which answers 200 with index.html. axios hands that over
 * as a happy result, and a screen that trusts it renders an HTML string as a
 * finding: "Unknown", a blank edibility, and translation keys where the
 * species should be. That is a far worse failure than an error card, because
 * it looks like the feature works badly rather than like something is wrong.
 *
 * The check is deliberately about SHAPE, not vocabulary: the server owns which
 * kinds and edibility values exist, and a second copy of those lists here
 * would be a second thing to forget to update.
 */
export function isAnswer(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
    return typeof data.kind === 'string'
        && typeof data.edibility === 'string'
        && typeof data.warning === 'string'
        && typeof data.high_risk === 'boolean';
}

/**
 * A translated string, never a translation key.
 *
 * `t()` returns the key itself when it has no entry, so a value the server can
 * produce but nobody translated reaches the user as "quickid.kind.shrub".
 * `test_identify_contract.py` stops that happening for the known vocabulary;
 * this stops it looking like machine output when something slips through
 * anyway.
 */
export function translated(t, key, fallback) {
    const value = t(key);
    return value === key ? fallback : value;
}

/** The base64 payload of a data URL, without the `data:image/jpeg;base64,` head. */
export function dataUrlToBase64(dataUrl) {
    const value = String(dataUrl || '');
    const comma = value.indexOf(',');
    return comma === -1 ? value : value.slice(comma + 1);
}
